import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getTools } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { isAuthenticated } from '../lib/auth';
import { Wrench, ArrowRight, Lock, Info } from 'lucide-react';
import LegalFooter from '../components/LegalFooter';

export default function Tools() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const authenticated = isAuthenticated();

  useEffect(() => {
    loadTools();
  }, []);

  const loadTools = async () => {
    setLoading(true);
    try {
      const response = await getTools('active');
      setTools(response.data);
    } catch (err) {
      console.error('Failed to load tools:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="tools-page">
      {/* Header */}
      <header className="border-b border-zinc-200">
        <div className="container mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Link to="/" className="header-logo text-xl">AI Accountability Standards</Link>
            </div>
            <nav className="flex items-center gap-6">
              <Link to="/registry" className="nav-link">Registry</Link>
              <Link to="/tools" className="nav-link nav-link-active">Tools</Link>
              <Link to="/org/login" className="text-sm text-zinc-500 hover:text-zinc-700">Organization Login</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-8 py-12">
        <div className="page-header">
          <h1 className="page-title">Verified Instruments</h1>
          <p className="page-subtitle">
            Standardized assessment tools aligned to published standards.
          </p>
        </div>

        {/* Tools Description */}
        <div className="bg-zinc-50 border border-zinc-200 p-4 mb-8">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-zinc-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-zinc-600">
              <ul className="space-y-1">
                <li>• Tools generate standardized, versioned reports aligned to published standards</li>
                <li>• Tools rely entirely on user-provided inputs</li>
                <li>• Outputs are estimates, not predictions or guarantees</li>
                <li>• Tools do not constitute legal, financial, or professional advice</li>
              </ul>
            </div>
          </div>
        </div>

        {!authenticated && (
          <div className="mb-8 p-4 bg-zinc-100 border border-zinc-300 flex items-center gap-3">
            <Lock className="w-5 h-5 text-zinc-400" />
            <p className="text-sm text-zinc-600">
              Organization login required to generate reports.{' '}
              <Link to="/org/login?returnUrl=/tools" className="text-zinc-900 underline">Sign in</Link>
            </p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="text-zinc-500">Loading tools...</div>
          </div>
        ) : tools.length === 0 ? (
          <div className="empty-state border border-zinc-200 p-12">
            <Wrench className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
            <p>No tools available.</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {tools.map(tool => (
              <div key={tool.id} className="border border-zinc-200 p-6 hover:border-zinc-400 transition-colors" data-testid={`tool-card-${tool.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="cert-id">{tool.id}</span>
                      <Badge variant="outline" className="rounded-none status-active">
                        {tool.status}
                      </Badge>
                    </div>
                    <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {tool.name}
                    </h2>
                    <p className="text-zinc-600 mb-4">{tool.description}</p>
                    <div className="flex items-center gap-4 text-sm text-zinc-500">
                      <span>Standard: <span className="font-mono">{tool.standard_id}</span></span>
                      <span>Version: <span className="font-mono">{tool.standard_version}</span></span>
                    </div>
                  </div>
                  <div className="ml-8">
                    {authenticated ? (
                      <Link to={`/tools/${tool.id}/run`}>
                        <Button className="btn-primary rounded-none" data-testid={`run-tool-${tool.id}`}>
                          Run Tool
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    ) : (
                      <Link to={`/org/login?returnUrl=/tools/${tool.id}/run`}>
                        <Button variant="outline" className="rounded-none" data-testid={`login-to-run-${tool.id}`}>
                          <Lock className="w-4 h-4 mr-2" />
                          Login to Run
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>

                {/* Input Schema Preview */}
                <div className="mt-6 pt-6 border-t border-zinc-200">
                  <h3 className="text-sm font-medium text-zinc-500 uppercase tracking-wide mb-3">Required Inputs</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(tool.inputs_schema || {}).map(([key, schema]) => (
                      <span 
                        key={key} 
                        className={`text-xs px-2 py-1 border ${schema.required ? 'bg-zinc-100 border-zinc-300' : 'bg-white border-zinc-200'}`}
                      >
                        {schema.label || key}
                        {schema.required && <span className="text-red-500 ml-0.5">*</span>}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-12 p-6 bg-zinc-50 border border-zinc-200">
          <p className="text-sm text-zinc-500">
            <strong>Disclaimer:</strong> These tools generate estimates based on user-provided inputs. 
            Results do not constitute predictions, guarantees, legal advice, financial advice, or professional recommendations. 
            Users are responsible for the accuracy of inputs and independent verification of outputs.
          </p>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
